import clsx from 'clsx';
import {
  useMemo,
  type JSX,
  type KeyboardEvent,
  type PropsWithChildren,
} from 'react';
import { toast } from 'react-toastify';

import {
  type DataTestId,
  type StylingProps,
} from '@dynatrace/strato-components/core';
import { Container, Flex } from '@dynatrace/strato-components/layouts';
import { _textStyleCSS as textStyleCSS } from '@dynatrace/strato-components/styles';
import { Text } from '@dynatrace/strato-components/typography';
import {
  CriticalIcon,
  InformationIcon,
  SuccessIcon,
  WarningIcon,
} from '@dynatrace/strato-icons';

import {
  toastCSS,
  toastFlexContainerCSS,
  toastIconCSS,
  toastTextCSS,
} from './Toast.sty.js';
import { OverlayStackingContext } from '../../core/contexts/OverlayStacking.js';

type ToastProps = PropsWithChildren<{
  type: 'info' | 'warning' | 'critical' | 'success';
  title: string;
  message?: string | JSX.Element;
  closeToast: () => void;
}> &
  StylingProps &
  DataTestId;

const Toast = (props: ToastProps) => {
  const {
    children,
    type,
    title,
    message,
    closeToast,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
  } = props;

  const Icon = useMemo(() => {
    switch (type) {
      case 'warning':
        return <WarningIcon />;
      case 'success':
        return <SuccessIcon />;
      case 'critical':
        return <CriticalIcon />;
      case 'info':
        return <InformationIcon />;
    }
  }, [type]);

  // Handle the escape key
  const closeToastOnEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      closeToast();
    }
  };

  return (
    <div
      data-testid={dataTestId}
      className={clsx(consumerClassName, toastCSS)}
      style={consumerStyle}
      onKeyDown={closeToastOnEsc}
    >
      {/*
      Our internal `OverlayStackingProvider` has some built in logic to only always
      increment by one layer. For actually creating the correct zIndex offset for the
      "always on top" toast, we need to add a couple more layers to it.
      */}
      <OverlayStackingContext.Provider value={100}>
        <Container
          variant="accent"
          color={type === 'info' ? 'primary' : type}
          className={toastIconCSS}
        >
          {Icon}
        </Container>
        <Flex
          className={clsx(
            toastFlexContainerCSS,
            textStyleCSS({ textStyle: 'base' }),
          )}
          paddingLeft={16}
          paddingTop={12}
          paddingBottom={12}
          paddingRight={8}
          flexDirection="column"
          alignItems="flex-start"
          flexGrow={1}
          gap={6}
        >
          {title && (
            <Text className={toastTextCSS} textStyle="base-emphasized" as="p">
              {title}
            </Text>
          )}
          {message && (
            <Text className={toastTextCSS} textStyle="base" as="p">
              {message}
            </Text>
          )}
          {children}
        </Flex>
      </OverlayStackingContext.Provider>
    </div>
  );
};

// #region Typings
/**
 * Options that can be given to the showToast function.
 * @public
 */
export interface ToastOptions extends StylingProps, DataTestId {
  /** A unique id for the toast. Using this toast id consumer can control
   *  the behavior of individual toast notifications.  Toast messages with same
   *  id will only be shown once.
   */
  id?: string | number;

  /** Title displayed in the toast notification. */
  title: string;
  /**
   * Type of the notification. Also indicates the color and icon.
   * @defaultValue 'info'
   * */
  type?: 'info' | 'warning' | 'critical' | 'success';
  /** Message displayed in the toast notification. */
  message?: string | JSX.Element;
  /**
   * The Toast notification will automatically close after a certain period of
   * time given in milliseconds. If 'infinite' is provided, the user must close the toast.
   * @defaultValue info and success: 8000, critical and warning: 'infinite'
   * */
  lifespan?: number | 'infinite';
  /**
   * Optional actions passed to the toast element, appended on the bottom left.
   * Should only be used to either add a Button or a Link.
   */
  actions?: JSX.Element;
  /**
   * Live region roles
   * @defaultValue info, success and warning: 'status', critical: 'alert'
   */
  role?: 'alert' | 'log' | 'status';
  /**
   * Position of toast
   * @defaultValue 'bottom-left'
   */
  position?: 'bottom-right' | 'bottom-center' | 'bottom-left';
}
// #endregion Typings

/**
 * Function to open a toast configuration.
 *
 * @returns the respective toast id
 * @public
 */
export function showToast({
  id,
  type = 'info',
  role,
  lifespan,
  title,
  message,
  position = 'bottom-left',
  className: consumerClassName,
  style: consumerStyle,
  'data-testid': dataTestId,
  actions,
}: ToastOptions): number | string {
  // default role according to type
  const defaultRole = type === 'critical' ? 'alert' : 'status';
  // default lifespan according to type
  const defaultAutoClose =
    type === 'warning' || type === 'critical' ? false : 8000;
  const autoClose = lifespan === 'infinite' ? false : lifespan;

  return toast(
    <Toast
      type={type}
      title={title}
      message={message}
      closeToast={() => undefined}
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
    >
      {actions}
    </Toast>,
    {
      toastId: id,
      autoClose: autoClose ?? defaultAutoClose,
      role: role ?? defaultRole,
      position,
    },
  );
}

/**
 * Function to close a single toast.
 *
 * @param id - id of the toast that should be dismissed
 * @public
 */
export function dismissToast(id: string | number): void {
  toast.dismiss(id);
}

/**
 * Function to close all open toasts at once.
 * @public
 */
export function dismissAllToasts(): void {
  toast.dismiss();
}
